/**
 * Spawn Manager - Handles entity spawning with terrain-aware placement
 * 
 * Features:
 * - Terrain-aware spawn positioning
 * - Random spawn point generation
 * - Spawn zones/regions
 * - Height clamping to terrain
 * - Spawn validation
 */

import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Ray } from "@babylonjs/core/Culling/ray";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";

import { IDisposable } from "../core/Engine.types";
import { TerrainManager } from "../terrain/index";
import { EntityManager, SpawnOptions } from "../entity/EntityManager";
import { Entity } from "../entity/Entity";

/**
 * Spawn zone definition
 */
export interface SpawnZone {
    name: string;
    center: Vector3;
    radius: number;
    minCount?: number;
    maxCount?: number;
    allowedEntities?: string[];
    excludedEntities?: string[];
}

/**
 * Spawn configuration
 */
export interface SpawnConfig {
    /** Height offset above terrain (for model pivot point) */
    heightOffset?: number;
    /** Use raycast for precise height detection */
    useRaycast?: boolean;
    /** Raycast start height */
    raycastHeight?: number;
    /** Align entity to terrain normal */
    alignToTerrain?: boolean;
    /** Maximum slope angle in degrees for valid spawn */
    maxSlopeAngle?: number;
}

/**
 * SpawnManager - Manages entity spawning with terrain awareness
 */
export class SpawnManager implements IDisposable {
    private _scene: Scene;
    private _entityManager: EntityManager;
    private _terrain: TerrainManager | null = null;
    private _spawnZones: Map<string, SpawnZone> = new Map();
    private _config: SpawnConfig;

    constructor(
        scene: Scene,
        entityManager: EntityManager,
        config?: SpawnConfig
    ) {
        this._scene = scene;
        this._entityManager = entityManager;
        this._config = {
            heightOffset: 0,
            useRaycast: true,
            raycastHeight: 100,
            alignToTerrain: false,
            maxSlopeAngle: 45,
            ...config,
        };
    }

    /**
     * Set the terrain manager for height queries
     */
    public setTerrain(terrain: TerrainManager): void {
        this._terrain = terrain;
    }

    /**
     * Spawn an entity at a position, automatically adjusting Y to terrain height
     */
    public async spawnOnTerrain(options: SpawnOptions): Promise<Entity> {
        const position = options.position || Vector3.Zero();
        
        // Adjust Y position to terrain height
        const terrainY = this.getTerrainHeightAt(position.x, position.z);
        const spawnPosition = new Vector3(
            position.x,
            terrainY + (this._config.heightOffset ?? 0),
            position.z
        );

        // Update spawn options with corrected position
        const adjustedOptions: SpawnOptions = {
            ...options,
            position: spawnPosition,
        };

        const entity = await this._entityManager.spawn(adjustedOptions);

        console.log(`SpawnManager: Spawned "${entity.name}" at terrain height ${terrainY.toFixed(2)}`);
        return entity;
    }

    /**
     * Spawn entity at random position within terrain bounds
     */
    public async spawnRandom(
        options: Omit<SpawnOptions, 'position'>,
        bounds?: { minX: number; maxX: number; minZ: number; maxZ: number }
    ): Promise<Entity> {
        const spawnBounds = bounds || this._getTerrainBounds();
        
        // Generate random position within bounds
        const x = spawnBounds.minX + Math.random() * (spawnBounds.maxX - spawnBounds.minX);
        const z = spawnBounds.minZ + Math.random() * (spawnBounds.maxZ - spawnBounds.minZ);

        return this.spawnOnTerrain({
            ...options,
            position: new Vector3(x, 0, z),
        });
    }

    /**
     * Spawn entity in a specific zone
     */
    public async spawnInZone(
        zoneName: string,
        options: Omit<SpawnOptions, 'position'>
    ): Promise<Entity | null> {
        const zone = this._spawnZones.get(zoneName);
        if (!zone) {
            console.warn(`SpawnManager: Zone "${zoneName}" not found`);
            return null;
        }

        // Generate random position within zone radius
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * zone.radius;
        const x = zone.center.x + Math.cos(angle) * distance;
        const z = zone.center.z + Math.sin(angle) * distance;

        return this.spawnOnTerrain({
            ...options,
            position: new Vector3(x, 0, z),
        });
    }

    /**
     * Get terrain height at world X/Z coordinates
     */
    public getTerrainHeightAt(x: number, z: number): number {
        if (!this._terrain) {
            console.warn("SpawnManager: No terrain manager set");
            return 0;
        }

        // First try the terrain manager's built-in method
        if (this._terrain.isReady) {
            const height = this._terrain.getHeightAtPosition(x, z);
            if (height !== 0 || this._isInsideTerrainBounds(x, z)) {
                return height;
            }
        }

        // Fallback to raycast if configured
        if (this._config.useRaycast) {
            return this._raycastTerrainHeight(x, z);
        }

        return 0;
    }

    /**
     * Get terrain normal at world X/Z coordinates
     */
    public getTerrainNormalAt(x: number, z: number): Vector3 {
        if (!this._terrain) {
            return Vector3.Up();
        }
        return this._terrain.getNormalAtPosition(x, z);
    }

    /**
     * Check if a position is valid for spawning
     */
    public isValidSpawnPosition(x: number, z: number): boolean {
        // Check if inside terrain bounds
        if (!this._isInsideTerrainBounds(x, z)) {
            return false;
        }

        // Check slope angle
        if (this._config.maxSlopeAngle !== undefined) {
            const normal = this.getTerrainNormalAt(x, z);
            const slopeAngle = Math.acos(Vector3.Dot(normal, Vector3.Up())) * (180 / Math.PI);
            if (slopeAngle > this._config.maxSlopeAngle) {
                return false;
            }
        }

        return true;
    }

    /**
     * Find a valid spawn position near a target
     */
    public findValidSpawnNear(
        target: Vector3,
        searchRadius: number = 10,
        attempts: number = 10
    ): Vector3 | null {
        for (let i = 0; i < attempts; i++) {
            const angle = Math.random() * Math.PI * 2;
            const distance = Math.random() * searchRadius;
            const x = target.x + Math.cos(angle) * distance;
            const z = target.z + Math.sin(angle) * distance;

            if (this.isValidSpawnPosition(x, z)) {
                const y = this.getTerrainHeightAt(x, z);
                return new Vector3(x, y, z);
            }
        }
        return null;
    }

    /**
     * Register a spawn zone
     */
    public addSpawnZone(zone: SpawnZone): void {
        this._spawnZones.set(zone.name, zone);
    }

    /**
     * Remove a spawn zone
     */
    public removeSpawnZone(name: string): void {
        this._spawnZones.delete(name);
    }

    /**
     * Get all spawn zones
     */
    public getSpawnZones(): SpawnZone[] {
        return Array.from(this._spawnZones.values());
    }

    /**
     * Clamp an entity's Y position to terrain height
     */
    public clampToTerrain(entity: Entity, heightOffset: number = 0): void {
        const pos = entity.position;
        const terrainY = this.getTerrainHeightAt(pos.x, pos.z);
        entity.position = new Vector3(pos.x, terrainY + heightOffset, pos.z);
    }

    /**
     * Raycast to find terrain height
     */
    private _raycastTerrainHeight(x: number, z: number): number {
        const rayStart = new Vector3(x, this._config.raycastHeight!, z);
        const rayDirection = Vector3.Down();
        const rayLength = this._config.raycastHeight! * 2;

        const ray = new Ray(rayStart, rayDirection, rayLength);
        
        // Pick against terrain mesh only
        const terrainMesh = this._terrain?.mesh;
        if (terrainMesh) {
            const hit = ray.intersectsMesh(terrainMesh as AbstractMesh, false);
            if (hit.hit && hit.pickedPoint) {
                return hit.pickedPoint.y;
            }
        }

        // Fallback: pick against all meshes
        const pickResult = this._scene.pickWithRay(ray, (mesh) => {
            return mesh.name.toLowerCase().includes("terrain") || 
                   mesh.name.toLowerCase().includes("ground");
        });

        if (pickResult?.hit && pickResult.pickedPoint) {
            return pickResult.pickedPoint.y;
        }

        return 0;
    }

    /**
     * Check if coordinates are inside terrain bounds
     */
    private _isInsideTerrainBounds(x: number, z: number): boolean {
        if (!this._terrain) return true;

        const config = this._terrain.config;
        const halfWidth = config.width / 2;
        const halfDepth = config.depth / 2;

        return x >= -halfWidth && x <= halfWidth && 
               z >= -halfDepth && z <= halfDepth;
    }

    /**
     * Get terrain bounds
     */
    private _getTerrainBounds(): { minX: number; maxX: number; minZ: number; maxZ: number } {
        if (!this._terrain) {
            return { minX: -50, maxX: 50, minZ: -50, maxZ: 50 };
        }

        const config = this._terrain.config;
        const halfWidth = config.width / 2;
        const halfDepth = config.depth / 2;

        return {
            minX: -halfWidth,
            maxX: halfWidth,
            minZ: -halfDepth,
            maxZ: halfDepth,
        };
    }

    /**
     * Dispose resources
     */
    public dispose(): void {
        this._spawnZones.clear();
        this._terrain = null;
    }
}
